import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Check } from "lucide-react";
import { useLang } from "@/landing/LanguageContext";
import { images } from "@/landing/data";
import { Reveal, Overline, CTAButton } from "@/landing/ui";

export const ImageBand = () => (
  <section className="w-full bg-earth py-12 lg:py-16" data-testid="image-band">
    <div className="mx-auto max-w-3xl px-6 lg:px-10">
      <div className="relative overflow-hidden">
        <img
          src={images.band}
          alt="Sesión de Activación Kundalini"
          className="mx-auto max-h-[70vh] w-full object-contain"
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-earth to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-earth to-transparent" />
      </div>
    </div>
  </section>
);


export const VideoSusana = () => {
  const { t } = useLang();
  const v = t.video;
  const [playing, setPlaying] = useState(false);

  return (
    <section className="bg-carbon py-24 text-cream lg:py-32" data-testid="video-section">
      <div className="mx-auto max-w-6xl px-6 lg:px-10">
        <Reveal className="mb-10 text-center">
          <h2 className="mx-auto mt-5 max-w-3xl font-serif text-3xl font-light leading-tight text-balance md:text-4xl lg:text-5xl">
            {v.title}
          </h2>
          {v.subtitle && (
            <p className="mx-auto mt-5 max-w-2xl font-sans text-base font-light leading-relaxed text-cream/70 lg:text-lg">
              {v.subtitle}
            </p>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <div className="group relative aspect-video w-full overflow-hidden border border-cream/10">
            {playing ? (
              <iframe
                data-testid="video-iframe"
                src={`https://player.vimeo.com/video/${v.videoId}?autoplay=1&title=0&byline=0&portrait=0`}
                className="h-full w-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={v.title}
              />
            ) : (
              <>
                <img
                  src={v.thumb || images.videoThumb}
                  alt={v.caption}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-carbon/40" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-b from-transparent to-carbon" />
                <button
                  onClick={() => setPlaying(true)}
                  data-testid="play-video-btn"
                  className="absolute inset-0 flex items-center justify-center"
                  aria-label={v.play}
                >
                  <span className="flex h-20 w-20 items-center justify-center rounded-full border border-cream/40 bg-carbon/30 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:border-sand">
                    <Play size={26} className="ml-1 fill-cream text-cream" />
                  </span>
                </button>
              </>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-8 flex flex-col items-center gap-6 text-center">
          <p className="font-sans text-sm tracking-wide text-cream/60">{v.caption}</p>
          <CTAButton testid="video-cta" variant="light">
            {t.ctas.talk}
          </CTAButton>
        </Reveal>

        {v.discover && (
          <Reveal delay={0.2} className="mx-auto mt-14 max-w-2xl border-t border-cream/10 pt-10">
            <p className="font-serif text-xl font-light text-sand lg:text-2xl">{v.discoverTitle}</p>
            <ul className="mt-6 space-y-4">
              {v.discover.map((d, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={18} className="mt-1 shrink-0 text-sand" />
                  <span className="font-sans text-base font-light leading-relaxed text-cream/85 lg:text-lg">
                    {d}
                  </span>
                </li>
              ))}
            </ul>
            {v.discoverClose && (
              <p className="mt-8 border-l-2 border-sand/60 pl-6 font-serif text-lg font-light italic leading-snug text-cream lg:text-xl">
                {v.discoverClose}
              </p>
            )}
          </Reveal>
        )}
      </div>
    </section>
  );
};

export const PainSection = () => {
  const { t } = useLang();
  const p = t.pain;
  return (
    <section className="bg-cream py-24 text-carbon lg:py-32" data-testid="pain-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="max-w-3xl">
          <Overline>{p.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight text-balance md:text-4xl lg:text-5xl">
            {p.title}
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <ul className="space-y-7">
              {p.points.map((point, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex gap-5 border-b border-earth/15 pb-7">
                    <span className="font-serif text-xl text-earth/40">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                      {point}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="relative lg:sticky lg:top-28">
              <div className="relative aspect-[4/5] w-full overflow-hidden">
                <img src={images.pain} alt="" className="h-full w-full object-cover" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-b from-transparent to-cream" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden h-32 w-32 border border-earth/30 lg:block" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const TrainedSection = () => {
  const { t } = useLang();
  const tr = t.trained;
  return (
    <section className="relative overflow-hidden bg-sand py-24 text-carbon lg:py-32" data-testid="trained-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Overline>{tr.overline}</Overline>
          <h2 className="mt-5 max-w-3xl font-serif text-3xl font-light leading-tight text-balance md:text-4xl lg:text-5xl">
            {tr.title}
          </h2>
          <div className="mt-7 flex flex-wrap gap-3">
            {tr.tags.map((tag) => (
              <span
                key={tag}
                className="border border-carbon/30 px-4 py-1.5 font-sans text-xs tracking-[0.15em] uppercase text-carbon/80"
              >
                {tag}
              </span>
            ))}
          </div>
          <p className="mt-10 max-w-2xl font-sans text-lg font-light text-earth">{tr.intro}</p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden border border-carbon/15 bg-carbon/15 sm:grid-cols-2">
          {tr.questions.map((q, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="flex h-full flex-col bg-sand p-8 transition-colors duration-300 hover:bg-beige lg:p-10">
                <span className="font-serif text-2xl text-earth/40">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-5 font-serif text-xl font-light leading-snug text-carbon lg:text-2xl">
                  {q.scenario}
                </p>
                <p className="mt-4 font-sans text-base font-light leading-relaxed text-earth lg:text-lg">
                  {q.question}
                </p>
                {q.note && (
                  <p className="mt-5 font-sans text-xs tracking-[0.18em] uppercase text-earth/70">
                    {q.note}
                  </p>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mx-auto mt-16 max-w-3xl text-center">
            <p className="font-serif text-2xl font-light italic leading-snug text-carbon lg:text-4xl">
              {tr.quote.lead}
            </p>
            <div className="mt-8 space-y-2">
              {tr.quote.lines.map((l, i) => (
                <p key={i} className="font-serif text-xl font-light text-earth lg:text-2xl">
                  {l}
                </p>
              ))}
            </div>
            <p className="mx-auto mt-8 max-w-2xl font-sans text-base font-light leading-relaxed text-carbon lg:text-lg">
              {tr.quote.close}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const BridgeSection = () => {
  const { t } = useLang();
  const b = t.bridge;
  return (
    <section className="bg-earth py-24 text-cream lg:py-32" data-testid="bridge-section">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <Reveal>
          <Overline dark>{b.overline}</Overline>
          <h2 className="mt-5 font-serif text-3xl font-light leading-tight text-balance md:text-4xl lg:text-5xl">
            {b.title}
          </h2>
          {b.subtitle && (
            <p className="mt-6 font-serif text-xl font-light italic leading-snug text-balance text-sand lg:text-2xl">
              {b.subtitle}
            </p>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 space-y-5">
            {b.paragraphs.map((para, i) => (
              <p
                key={i}
                className="font-sans text-base font-light leading-relaxed text-cream/80 lg:text-lg"
              >
                {para}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-12 font-serif text-2xl font-light italic leading-snug text-sand lg:text-3xl">
            {b.midHeading}
          </p>
          <div className="mt-8 space-y-5">
            {b.paragraphs2.map((para, i) => (
              <p
                key={i}
                className="font-sans text-base font-light leading-relaxed text-cream/80 lg:text-lg"
              >
                {para}
              </p>
            ))}
          </div>
          <ul className="mt-7 space-y-3">
            {b.bullets.map((it, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check size={18} className="mt-1 shrink-0 text-sand" />
                <span className="font-sans text-base font-light leading-relaxed text-cream lg:text-lg">
                  {it}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};
